import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  myTeamsApi,
  teamsApi,
  type TeamAssignBrandInput,
  type TeamCreateInput,
  type TeamInviteUserInput,
  type TeamUpdateInput,
} from '@/api/teams';
import { usersApi, type ManagedUser, type ManagedUserScope, type UsersListResult } from '@/api/users';
import type { ApiError, AppRole, TeamBrand } from '@/types';

export const teamKeys = {
  all: ['teams'] as const,
  list: () => [...teamKeys.all, 'list'] as const,
  detail: (id: number) => [...teamKeys.all, 'detail', id] as const,
  audit: (id: number) => [...teamKeys.all, 'audit', id] as const,
  mine: () => [...teamKeys.all, 'mine'] as const,
  mineDetail: (id: number) => [...teamKeys.all, 'mine', 'detail', id] as const,
  mineUsers: (id: number) => [...teamKeys.all, 'mine', 'users', id] as const,
};

export const userKeys = {
  all: ['users'] as const,
  list: (scope: ManagedUserScope) => [...userKeys.all, 'list', scope] as const,
};

// ---------------------------------------------------------------------------
// Admin: /admin/teams
// ---------------------------------------------------------------------------

export function useTeams(enabled = true) {
  return useQuery({
    queryKey: teamKeys.list(),
    queryFn: () => teamsApi.list(),
    enabled,
  });
}

export function useTeam(id: number | undefined) {
  return useQuery({
    queryKey: teamKeys.detail(id ?? 0),
    queryFn: () => teamsApi.getById(id as number),
    enabled: id !== undefined,
  });
}

export function useTeamAudit(id: number | undefined) {
  return useQuery({
    queryKey: teamKeys.audit(id ?? 0),
    queryFn: () => teamsApi.getAudit(id as number),
    enabled: id !== undefined,
  });
}

function useInvalidateTeams() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: teamKeys.all });
    void queryClient.invalidateQueries({ queryKey: userKeys.all });
  };
}

export function useCreateTeam() {
  const invalidate = useInvalidateTeams();
  return useMutation({
    mutationFn: (input: TeamCreateInput) => teamsApi.create(input),
    onSuccess: invalidate,
  });
}

export function useUpdateTeam() {
  const invalidate = useInvalidateTeams();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: TeamUpdateInput }) =>
      teamsApi.update(id, input),
    onSuccess: invalidate,
  });
}

export function useAssignTeamBrand() {
  const invalidate = useInvalidateTeams();
  return useMutation({
    mutationFn: ({ id, ...input }: { id: number } & TeamAssignBrandInput) =>
      teamsApi.assignBrand(id, input),
    onSuccess: invalidate,
  });
}

export function useSetTeamBrandPrimary() {
  const invalidate = useInvalidateTeams();
  return useMutation({
    mutationFn: ({ id, brandId, isPrimary }: { id: number; brandId: number; isPrimary: boolean }) =>
      teamsApi.setBrandPrimary(id, brandId, isPrimary),
    onSuccess: invalidate,
  });
}

export function useRemoveTeamBrand() {
  const invalidate = useInvalidateTeams();
  return useMutation({
    mutationFn: ({ id, brandId }: { id: number; brandId: number }) =>
      teamsApi.removeBrand(id, brandId),
    onSuccess: invalidate,
  });
}

export function useAssignTeamUser() {
  const invalidate = useInvalidateTeams();
  return useMutation({
    mutationFn: ({ id, userId }: { id: number; userId: number }) => teamsApi.assignUser(id, userId),
    onSuccess: invalidate,
  });
}

export function useRemoveTeamUser() {
  const invalidate = useInvalidateTeams();
  return useMutation({
    mutationFn: ({ id, userId }: { id: number; userId: number }) => teamsApi.removeUser(id, userId),
    onSuccess: invalidate,
  });
}

/**
 * Admin invite. The backend invite endpoint adds the new user to exactly one
 * team (the one in the route); additional teams are attached afterwards via
 * the regular assign-user endpoint using the returned userId.
 */
export function useInviteTeamUser() {
  const invalidate = useInvalidateTeams();
  return useMutation({
    mutationFn: async ({ teamIds, input }: { teamIds: number[]; input: TeamInviteUserInput }) => {
      if (teamIds.length === 0) throw new Error('Select at least one team.');
      const [firstTeamId, ...rest] = teamIds;
      const user = await teamsApi.inviteUser(firstTeamId, input);
      for (const teamId of rest) {
        await teamsApi.assignUser(teamId, user.userId);
      }
      return user;
    },
    onSuccess: invalidate,
  });
}

// ---------------------------------------------------------------------------
// Editor self-service: /my/teams
// ---------------------------------------------------------------------------

export function useMyTeams(enabled = true) {
  return useQuery({
    queryKey: teamKeys.mine(),
    queryFn: () => myTeamsApi.list(),
    enabled,
  });
}

export function useMyTeamUsers(id: number | undefined) {
  return useQuery({
    queryKey: teamKeys.mineUsers(id ?? 0),
    queryFn: () => myTeamsApi.getUsers(id as number),
    enabled: id !== undefined,
  });
}

/** Editor invite — the team is fixed by the route, the role must rank below the caller's. */
export function useInviteMyTeamUser() {
  const invalidate = useInvalidateTeams();
  return useMutation({
    mutationFn: ({ teamId, input }: { teamId: number; input: TeamInviteUserInput }) =>
      myTeamsApi.inviteUser(teamId, input),
    onSuccess: invalidate,
  });
}

export function useRemoveMyTeamUser() {
  const invalidate = useInvalidateTeams();
  return useMutation({
    mutationFn: ({ teamId, userId }: { teamId: number; userId: number }) =>
      myTeamsApi.removeUser(teamId, userId),
    onSuccess: invalidate,
  });
}

// ---------------------------------------------------------------------------
// User Management (/admin/users, /my/users) — Auth0 data is merged server-side
// ---------------------------------------------------------------------------

export function useManagedUsers(scope: ManagedUserScope, enabled = true) {
  return useQuery({
    queryKey: userKeys.list(scope),
    queryFn: () => usersApi.list(scope),
    enabled,
  });
}

/**
 * Patch a single row in every cached user list with the row the mutation just
 * returned, instead of invalidating the list. The write endpoints already
 * return the fresh Auth0-owned fields, so re-fetching the whole list would
 * only cost another round of Auth0 Management API calls (and risks a 429
 * right after the write call that just hit it).
 */
function useUpdateUserInCache() {
  const queryClient = useQueryClient();
  return (updated: ManagedUser) => {
    queryClient.setQueriesData<UsersListResult>({ queryKey: userKeys.all }, (old) =>
      old
        ? { ...old, users: old.users.map((u) => (u.userId === updated.userId ? updated : u)) }
        : old,
    );
  };
}

export function useUpdateUserRole(scope: ManagedUserScope) {
  const updateInCache = useUpdateUserInCache();
  return useMutation({
    mutationFn: ({ userId, role }: { userId: number; role: AppRole }) =>
      usersApi.updateRole(scope, userId, role),
    onSuccess: updateInCache,
  });
}

export function useUpdateUserStatus() {
  const updateInCache = useUpdateUserInCache();
  return useMutation({
    mutationFn: ({ userId, isBlocked }: { userId: number; isBlocked: boolean }) =>
      usersApi.updateStatus(userId, isBlocked),
    onSuccess: updateInCache,
  });
}

export function useUpdateUserTeams() {
  const updateInCache = useUpdateUserInCache();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, teamIds }: { userId: number; teamIds: number[] }) =>
      usersApi.updateTeams(userId, teamIds),
    onSuccess: (updated) => {
      updateInCache(updated);
      void queryClient.invalidateQueries({ queryKey: teamKeys.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Team → brand map for the current user (drives the cascading filter bar)
// ---------------------------------------------------------------------------

export interface TeamBrandsResult {
  /** Team → brand list (primary flag included) for every team visible to the caller. */
  teams: Array<{ brands: TeamBrand[] }>;
  isLoading: boolean;
  /**
   * True when team brand data could not be obtained for this user (e.g. the
   * endpoint is unavailable). Callers should fall back to the plain brand list.
   */
  unavailable: boolean;
}

/**
 * Resolves each of the caller's own teams (not every team in the org — an
 * admin belongs to teams the same way anyone else does) to its brand list via
 * `/my/teams` + `/my/teams/{id}/brands`, both available to every app role
 * (crm:viewer and above). Any 403/404 (endpoint missing) is treated as "no
 * team data" rather than an error so the filter bar can degrade gracefully.
 */
export function useMyTeamBrands(): TeamBrandsResult {
  const myTeams = useMyTeams();

  const summaries = myTeams.data ?? [];
  const listLoading = myTeams.isLoading;
  const listUnavailable = myTeams.isError;

  const details = useQueries({
    queries: summaries
      .filter((t) => t.isActive)
      .map((t) => ({
        queryKey: teamKeys.mineDetail(t.teamId),
        queryFn: () => myTeamsApi.getBrands(t.teamId),
        retry: (failureCount: number, err: unknown) => {
          const status = (err as ApiError | undefined)?.status;
          return status !== 403 && status !== 404 && failureCount < 2;
        },
        staleTime: 5 * 60 * 1000,
      })),
  });

  const anyDetailLoading = details.some((q) => q.isLoading);
  const allDetailsFailed = details.length > 0 && details.every((q) => q.isError);
  const teams = details.flatMap((q) => (q.data ? [{ brands: q.data }] : []));

  return {
    teams,
    isLoading: listLoading || anyDetailLoading,
    unavailable: listUnavailable || allDetailsFailed,
  };
}
